// src/components/audio/AudioPlayer.js
//
// Custom audio player for DataQuad's Audio Audit workspace.
//
// Used in two surfaces:
//   1. AudioAudit tab — large player, full controls, keyboard shortcuts
//   2. Inline cell preview (v2) — compact mode via `compact` prop
//
// Design language matches AILoadingOverlay tokens (Outfit font, brand cyan/gold,
// dark/light mode via useTheme).
//
// PLAYBACK MECHANIC (v1):
//   - Resolves absolute path: mediaDir + cell value (with normalization)
//   - Reads bytes via Tauri `read_file_bytes` command
//   - Wraps in Blob → URL.createObjectURL → HTML5 <audio>
//   - Revokes blob URL on src change / unmount to avoid leaks
//
// FUTURE (v2):
//   - Custom Tauri protocol `dataquad://media/...` for streaming (no full-file load)
//   - Waveform visualization (wavesurfer.js)
//   - Persisted reviewer notes
//
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '../../utils/ThemeSwitcher';
import { getAudioFormat } from './audioFormats';
import {
  Play, Pause, SkipBack, SkipForward, Volume2, VolumeX,
  AlertCircle, Loader2, FileAudio, Info,
} from 'lucide-react';

// ── Shared formatter for mm:ss and h:mm:ss ─────────────────────────────────
const formatTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
};

// ── Resolve a relative cell value (e.g. "media\AA_xxx.m4a") to absolute ────
// Handles: forward/back slash mix, leading "media\" or "media/" stripped if
// the mediaDir already ends in "/media", duplicate path component dedup.
export const resolveAudioPath = (mediaDir, cellValue) => {
  if (!mediaDir || !cellValue) return null;
  // Normalize separators
  const cellNorm = String(cellValue).replace(/\\/g, '/').trim();
  if (!cellNorm) return null;
  // Strip leading "media/" if mediaDir already ends in "media" or "/media"
  const dirLower = mediaDir.toLowerCase().replace(/\\/g, '/').replace(/\/+$/, '');
  let cellSafe = cellNorm.replace(/^\/+/, '');
  if (dirLower.endsWith('/media') && /^media\//i.test(cellSafe)) {
    cellSafe = cellSafe.replace(/^media\//i, '');
  }
  // Determine the OS separator from mediaDir (presence of backslash)
  const sep = mediaDir.includes('\\') ? '\\' : '/';
  const dirNorm = mediaDir.replace(/[\\/]+$/, '');
  return `${dirNorm}${sep}${cellSafe.replace(/\//g, sep)}`;
};

// ── Custom waveform peak extraction ────────────────────────────────────────
// Decodes audio bytes into PCM via Web Audio API, then bins amplitudes into
// `bins` peaks (default 700, matching the typical AudioPlayer width). Returns
// a Float32Array of normalized peaks (0..1) or null if decode fails.
//
// Why custom rather than wavesurfer.js / peaks.js?
//   - No external dependency to maintain or audit
//   - Easier to match DataQuad's gradient color palette in pure SVG
//   - Easier to integrate with our existing scrubber event handlers
//   - Lighter — adds <100 lines vs ~75KB of library code
//
// Performance note: decodeAudioData on a 50MB / 35-min audio file takes
// 200-800ms on modern hardware. We run it AFTER playback is ready, so the
// user can already listen while peaks render in.
let _sharedAudioCtx = null;
const getAudioContext = () => {
  if (!_sharedAudioCtx) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    _sharedAudioCtx = new Ctx();
  }
  return _sharedAudioCtx;
};

export const decodePeaks = async (arrayBuffer, bins = 700) => {
  const ctx = getAudioContext();
  if (!ctx) return null;
  let audioBuffer;
  try {
    audioBuffer = await ctx.decodeAudioData(arrayBuffer);
  } catch (e) {
    return null;
  }
  if (!audioBuffer) return null;

  // Mix down all channels into a single peak per bin. We compute the
  // per-bin max(abs) which produces the classic envelope look.
  const channels = audioBuffer.numberOfChannels;
  const length = audioBuffer.length;
  const samplesPerBin = Math.max(1, Math.floor(length / bins));
  const peaks = new Float32Array(bins);

  // Pull the longest channel out as a reference
  const data = audioBuffer.getChannelData(0);
  const ch1 = channels > 1 ? audioBuffer.getChannelData(1) : null;

  let maxObserved = 0;
  for (let b = 0; b < bins; b++) {
    const start = b * samplesPerBin;
    const end = Math.min(start + samplesPerBin, length);
    let peak = 0;
    for (let i = start; i < end; i++) {
      const v = ch1 ? Math.max(Math.abs(data[i]), Math.abs(ch1[i])) : Math.abs(data[i]);
      if (v > peak) peak = v;
    }
    peaks[b] = peak;
    if (peak > maxObserved) maxObserved = peak;
  }

  // Normalize so the loudest peak hits 1.0. Avoids tiny waveforms when
  // the source was recorded with low gain (common with phone mics).
  if (maxObserved > 0) {
    const scale = 1 / maxObserved;
    for (let b = 0; b < bins; b++) peaks[b] *= scale;
  }
  return peaks;
};

// ── Audio Player tokens — harmonized with AILoadingOverlay ─────────────────
const buildTokens = (isDark) => isDark
  ? {
      shellBg:    'rgba(255,255,255,0.02)',
      shellBdr:   'rgba(255,255,255,0.06)',
      track:      'rgba(255,255,255,0.08)',
      trackFill:  'linear-gradient(90deg, #1B6BE0 0%, #38B6FF 50%, #F5B547 100%)',
      thumb:      '#38B6FF',
      thumbGlow:  '0 0 12px rgba(56,182,255,0.5)',
      text:       '#E7EAF0',
      textSec:    '#B8BDC9',
      textMuted:  '#6B7180',
      textDim:    '#3A3F4A',
      btnBg:      'rgba(255,255,255,0.04)',
      btnBgHover: 'rgba(255,255,255,0.08)',
      btnActive:  'rgba(56,182,255,0.18)',
      accent:     '#38B6FF',
      success:    '#10B981',
      error:      '#EF4444',
      errorBg:    'rgba(239,68,68,0.10)',
    }
  : {
      shellBg:    'rgba(0,0,0,0.02)',
      shellBdr:   'rgba(0,0,0,0.07)',
      track:      'rgba(0,0,0,0.08)',
      trackFill:  'linear-gradient(90deg, #1453B0 0%, #1B6BE0 50%, #D9892C 100%)',
      thumb:      '#1B6BE0',
      thumbGlow:  '0 0 10px rgba(27,107,224,0.45)',
      text:       '#15181D',
      textSec:    '#3A3F4A',
      textMuted:  '#6B7180',
      textDim:    '#A8AEBA',
      btnBg:      'rgba(0,0,0,0.04)',
      btnBgHover: 'rgba(0,0,0,0.08)',
      btnActive:  'rgba(27,107,224,0.10)',
      accent:     '#1B6BE0',
      success:    '#047857',
      error:      '#DC2626',
      errorBg:    'rgba(220,38,38,0.08)',
    };

const SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

/**
 * AudioPlayer
 * Props:
 *   audioPath:    string | null   absolute path to audio file (resolved by parent)
 *   filename:     string          display name (cell value)
 *   tauriApi:     { invoke }      desktop bridge for file reads
 *   compact:      bool            (v2) smaller layout for inline cell use
 *   onLoadError:  (err) => void   parent notified when file fails (e.g. missing)
 *   keyboardScope: bool           if true, register window-level keyboard shortcuts
 *                                 (default true for audit tab; false for inline)
 */
const AudioPlayer = ({
  audioPath, filename, tauriApi,
  compact = false, onLoadError, keyboardScope = true,
  onTimeChange,            // (currentTime: number) => void — fires on play position change
  seekRequest,             // { to: number, key: any } — when key changes, seeks to `to`
}) => {
  const { isDarkMode } = useTheme();
  const tokens = buildTokens(isDarkMode);

  const audioRef = useRef(null);
  const blobUrlRef = useRef(null);

  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [formatWarning, setFormatWarning] = useState(null); // { ext, mime, description } | null
  const [waveformPeaks, setWaveformPeaks] = useState(null); // Float32Array of normalized peaks (0..1) | null
  const [isPlaying, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration]       = useState(0);
  const [volume, setVolume]           = useState(1);
  const [muted, setMuted]             = useState(false);
  const [speed, setSpeed]             = useState(1);
  const [isScrubbing, setScrubbing]   = useState(false);

  // ── Load file: read bytes via Tauri, wrap in blob URL ────────────────────
  useEffect(() => {
    let cancelled = false;
    const cleanup = () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };

    if (!audioPath || !tauriApi?.invoke) {
      cleanup();
      setError(audioPath ? 'Audio playback unavailable in this environment.' : null);
      setFormatWarning(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setFormatWarning(null);
    setWaveformPeaks(null);
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);

    (async () => {
      try {
        const bytes = await tauriApi.invoke('read_file_bytes', { path: audioPath });
        if (cancelled) return;
        // bytes from Tauri can come back as: ArrayBuffer, Uint8Array, or
        // (rarely) plain array of numbers depending on serializer config.
        let view;
        if (bytes instanceof Uint8Array) {
          view = bytes;
        } else if (bytes instanceof ArrayBuffer) {
          view = new Uint8Array(bytes);
        } else if (Array.isArray(bytes)) {
          view = new Uint8Array(bytes);
        } else {
          throw new Error('Unexpected byte format from sidecar.');
        }
        const fmt = getAudioFormat(filename || audioPath);
        const blob = new Blob([view], { type: fmt?.mime || 'audio/mpeg' });
        const url = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        cleanup(); // revoke any prior blob URL before assigning new one
        blobUrlRef.current = url;
        if (audioRef.current) {
          audioRef.current.src = url;
          audioRef.current.load();
        }
        // Surface a soft warning (non-blocking) when the format has known
        // patchy browser support — playback may still work but might not.
        setFormatWarning(fmt && !fmt.reliable ? fmt : null);
        setLoading(false);

        // ── Background waveform decode ──────────────────────────────────
        // Pull peaks out of the file via Web Audio API while the user
        // can already start playback. If decoding fails (some formats
        // can play in HTML5 audio but not decode in Web Audio API),
        // we silently fall back to the gradient bar — no user-facing error.
        // Clone the bytes to a fresh ArrayBuffer because decodeAudioData
        // takes ownership of the buffer (it becomes detached after decode).
        const bytesForDecode = view.buffer.slice(
          view.byteOffset,
          view.byteOffset + view.byteLength
        );
        decodePeaks(bytesForDecode, 700)
          .then((peaks) => {
            if (!cancelled && peaks) setWaveformPeaks(peaks);
          })
          .catch(() => {
            // Silent fallback — keep the gradient bar
          });
      } catch (e) {
        if (cancelled) return;
        const msg = String(e?.message || e);
        // Friendly text for common cases
        const friendly = /No such file|cannot find|os error 2|not found/i.test(msg)
          ? 'Audio file not found on disk. The media folder may have moved or the file was removed.'
          : `Could not load audio: ${msg}`;
        setError(friendly);
        setLoading(false);
        if (onLoadError) onLoadError(e);
      }
    })();

    return () => { cancelled = true; };
    // We intentionally exclude onLoadError to avoid re-fetching when the
    // parent's callback identity changes.
    // eslint-disable-next-line
  }, [audioPath, tauriApi, filename]);

  // ── Cleanup blob URL on unmount ───────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  // ── External seek requests (e.g. from NotesPanel "Jump to 12:34") ────────
  // Keyed seek: when seekRequest.key changes, seek to seekRequest.to. This
  // pattern lets the parent re-issue a seek to the same timestamp (the user
  // can click the same note's timestamp twice and it'll re-seek both times).
  useEffect(() => {
    if (!seekRequest || !audioRef.current) return;
    const t = Number(seekRequest.to);
    if (!Number.isFinite(t) || t < 0) return;
    audioRef.current.currentTime = t;
    setCurrentTime(t);
    if (onTimeChange) onTimeChange(t);
  // We intentionally only re-run when the key changes
  // eslint-disable-next-line
  }, [seekRequest?.key]);

  // ── HTML audio event handlers ─────────────────────────────────────────────
  const onLoadedMeta = () => {
    if (audioRef.current) {
      const d = audioRef.current.duration;
      setDuration(Number.isFinite(d) ? d : 0);
    }
  };
  const lastTimeEmitRef = useRef(0);
  const onTimeUpdate = () => {
    if (audioRef.current && !isScrubbing) {
      const t = audioRef.current.currentTime;
      setCurrentTime(t);
      // Throttle parent notifications to ~4Hz — enough for "Add note" UI
      // to feel live without storming the parent with 60Hz re-renders.
      const now = Date.now();
      if (onTimeChange && now - lastTimeEmitRef.current > 250) {
        lastTimeEmitRef.current = now;
        onTimeChange(t);
      }
    }
  };
  const onEnded = () => {
    setPlaying(false);
    setCurrentTime(audioRef.current?.duration || 0);
  };

  // ── Controls ──────────────────────────────────────────────────────────────
  const togglePlay = useCallback(() => {
    const a = audioRef.current;
    if (!a || !a.src) return;
    if (a.paused) {
      a.play().then(() => setPlaying(true)).catch((e) => {
        setError(`Playback failed: ${e.message || e}`);
      });
    } else {
      a.pause();
      setPlaying(false);
    }
  }, []);

  const skip = useCallback((delta) => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = Math.max(0, Math.min(a.duration || 0, a.currentTime + delta));
    setCurrentTime(a.currentTime);
  }, []);

  const setSpeedBy = useCallback((delta) => {
    setSpeed((s) => {
      const idx = SPEED_OPTIONS.indexOf(s);
      const next = SPEED_OPTIONS[Math.max(0, Math.min(SPEED_OPTIONS.length - 1, idx + delta))];
      if (audioRef.current) audioRef.current.playbackRate = next;
      return next;
    });
  }, []);

  const handleSpeedClick = (s) => {
    setSpeed(s);
    if (audioRef.current) audioRef.current.playbackRate = s;
  };

  const handleSeek = (clientX, rect) => {
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const t = ratio * (duration || 0);
    if (audioRef.current) {
      audioRef.current.currentTime = t;
      setCurrentTime(t);
      if (onTimeChange) onTimeChange(t);
    }
  };

  const handleVolume = (v) => {
    setVolume(v);
    if (audioRef.current) audioRef.current.volume = v;
    if (v === 0) setMuted(true);
    else setMuted(false);
  };

  const toggleMute = () => {
    if (audioRef.current) {
      const next = !muted;
      audioRef.current.muted = next;
      setMuted(next);
    }
  };

  // ── Keyboard shortcuts ────────────────────────────────────────────────────
  useEffect(() => {
    if (!keyboardScope) return;
    const onKey = (e) => {
      // Don't hijack when typing in inputs
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
      switch (e.key.toLowerCase()) {
        case ' ':
          e.preventDefault();
          togglePlay();
          break;
        case 'j':
          e.preventDefault();
          skip(-10);
          break;
        case 'l':
          e.preventDefault();
          skip(10);
          break;
        case 'arrowleft':
          e.preventDefault();
          skip(-5);
          break;
        case 'arrowright':
          e.preventDefault();
          skip(5);
          break;
        case ',':
        case '<':
          e.preventDefault();
          setSpeedBy(-1);
          break;
        case '.':
        case '>':
          e.preventDefault();
          setSpeedBy(1);
          break;
        case 'm':
          e.preventDefault();
          toggleMute();
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  // eslint-disable-next-line
  }, [togglePlay, skip, setSpeedBy]);

  // ── Render ────────────────────────────────────────────────────────────────
  const progressPct = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div style={{
      background: tokens.shellBg,
      border: `1px solid ${tokens.shellBdr}`,
      borderRadius: '12px',
      padding: compact ? '10px 12px' : '18px 20px',
      fontFamily: "'Outfit', system-ui, -apple-system, sans-serif",
      width: '100%',
      boxSizing: 'border-box',
    }}>
      {/* Filename header */}
      {!compact && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          marginBottom: '14px',
        }}>
          <div style={{
            width: '32px', height: '32px', borderRadius: '8px',
            background: tokens.btnActive,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: tokens.accent, flexShrink: 0,
          }}>
            <FileAudio size={16} />
          </div>
          <div style={{
            flex: 1, minWidth: 0, fontSize: '12.5px',
            color: tokens.textSec,
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {filename || '—'}
          </div>
          {duration > 0 && (
            <div style={{ fontSize: '11.5px', color: tokens.textMuted, flexShrink: 0 }}>
              {formatTime(duration)}
            </div>
          )}
        </div>
      )}

      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        onLoadedMetadata={onLoadedMeta}
        onTimeUpdate={onTimeUpdate}
        onEnded={onEnded}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        preload="metadata"
      />

      {/* Error / loading states */}
      {error && (
        <div style={{
          padding: '10px 12px',
          background: tokens.errorBg,
          border: `1px solid ${tokens.error}33`,
          borderRadius: '8px',
          fontSize: '12.5px',
          color: tokens.text,
          display: 'flex', alignItems: 'flex-start', gap: '8px',
        }}>
          <AlertCircle size={14} style={{ color: tokens.error, marginTop: '1px', flexShrink: 0 }} />
          <span style={{ flex: 1 }}>{error}</span>
        </div>
      )}

      {!error && formatWarning && (
        <div style={{
          padding: '8px 12px',
          marginBottom: compact ? '8px' : '12px',
          background: tokens.btnBg,
          border: `1px solid ${tokens.shellBdr}`,
          borderRadius: '8px',
          fontSize: '11.5px',
          color: tokens.textSec,
          display: 'flex', alignItems: 'flex-start', gap: '8px',
          lineHeight: 1.5,
        }}>
          <Info size={13} style={{ color: tokens.textMuted, marginTop: '1px', flexShrink: 0 }} />
          <span style={{ flex: 1 }}>
            {formatWarning.description}. Playback support varies — if it fails to play, the file may need to be converted to a more common format (mp3, m4a, wav).
          </span>
        </div>
      )}

      {!error && (
        <>
          {/* Waveform-based scrubber (falls back to gradient bar
              before peaks finish decoding) */}
          <Waveform
            peaks={waveformPeaks}
            progressPct={progressPct}
            currentTime={currentTime}
            duration={duration}
            tokens={tokens}
            compact={compact}
            disabled={loading || !duration}
            onScrubStart={() => setScrubbing(true)}
            onScrub={handleSeek}
            onScrubEnd={() => setScrubbing(false)}
          />

          {/* Time labels */}
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            fontSize: '11.5px', color: tokens.textMuted,
            fontVariantNumeric: 'tabular-nums',
            marginTop: '6px', marginBottom: compact ? '8px' : '14px',
          }}>
            <span style={{ color: tokens.textSec, fontWeight: 500 }}>
              {formatTime(currentTime)}
            </span>
            <span>{formatTime(duration)}</span>
          </div>

          {/* Controls */}
          <div style={{
            display: 'flex', alignItems: 'center',
            gap: compact ? '6px' : '10px',
            flexWrap: 'wrap',
          }}>
            <IconButton tokens={tokens} onClick={() => skip(-15)} title="Back 15s (J)" disabled={loading}>
              <SkipBack size={15} />
            </IconButton>
            <PlayButton tokens={tokens} isPlaying={isPlaying} loading={loading} onClick={togglePlay} />
            <IconButton tokens={tokens} onClick={() => skip(15)} title="Forward 15s (L)" disabled={loading}>
              <SkipForward size={15} />
            </IconButton>

            {/* Speed selector */}
            <SpeedSelector tokens={tokens} value={speed} onChange={handleSpeedClick} compact={compact} />

            {/* Volume */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              marginLeft: compact ? '0' : 'auto',
            }}>
              <IconButton tokens={tokens} onClick={toggleMute} title={muted ? 'Unmute (M)' : 'Mute (M)'}>
                {muted || volume === 0 ? <VolumeX size={15} /> : <Volume2 size={15} />}
              </IconButton>
              {!compact && (
                <input
                  type="range" min={0} max={1} step={0.01} value={muted ? 0 : volume}
                  onChange={(e) => handleVolume(parseFloat(e.target.value))}
                  style={{
                    width: '70px', accentColor: tokens.accent,
                    cursor: 'pointer',
                  }}
                />
              )}
            </div>
          </div>

          {/* Keyboard hints */}
          {!compact && keyboardScope && (
            <div style={{
              marginTop: '14px', paddingTop: '12px',
              borderTop: `1px solid ${tokens.shellBdr}`,
              fontSize: '10.5px', color: tokens.textMuted,
              display: 'flex', flexWrap: 'wrap', gap: '4px 14px',
            }}>
              <Hint tokens={tokens} keys="Space" label="Play/pause" />
              <Hint tokens={tokens} keys="J / L" label="Skip ±10s" />
              <Hint tokens={tokens} keys="← →" label="Skip ±5s" />
              <Hint tokens={tokens} keys=", ." label="Speed −/+" />
              <Hint tokens={tokens} keys="M" label="Mute" />
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────

const Waveform = ({ peaks, progressPct, currentTime, duration, tokens, compact, disabled, onScrubStart, onScrub, onScrubEnd }) => {
  const barRef = useRef(null);
  const [hoverX, setHoverX] = useState(null);
  const [hoverTime, setHoverTime] = useState(0);

  // Same scrubbing handlers as ScrubBar
  const onPointerDown = (e) => {
    if (disabled || !barRef.current) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    onScrubStart?.();
    onScrub(e.clientX, barRef.current.getBoundingClientRect());
    const onMove = (ev) => {
      if (!barRef.current) return;
      onScrub(ev.clientX, barRef.current.getBoundingClientRect());
    };
    const onUp = () => {
      onScrubEnd?.();
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const onMove = (e) => {
    if (disabled || !barRef.current) return;
    const rect = barRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, x / rect.width));
    setHoverX(x);
    setHoverTime(ratio * (duration || 0));
  };

  // If peaks aren't ready yet, fall back to the gradient bar — same look
  // the player has had since v1, just acting as a temporary stand-in.
  if (!peaks || peaks.length === 0) {
    return (
      <ScrubBar
        progressPct={progressPct}
        currentTime={currentTime}
        duration={duration}
        tokens={tokens}
        disabled={disabled}
        onScrubStart={onScrubStart}
        onScrub={onScrub}
        onScrubEnd={onScrubEnd}
      />
    );
  }

  // Build SVG bars from peaks. Each peak is a normalized 0..1 amplitude.
  const height = compact ? 36 : 56;
  const barWidth = 2;
  const barGap = 1;
  const totalBars = peaks.length;
  const viewWidth = totalBars * (barWidth + barGap);

  return (
    <div
      ref={barRef}
      onPointerDown={onPointerDown}
      onPointerMove={onMove}
      onPointerLeave={() => setHoverX(null)}
      style={{
        position: 'relative',
        height: `${height}px`,
        cursor: disabled ? 'default' : 'pointer',
        userSelect: 'none',
        opacity: disabled ? 0.4 : 1,
        // Subtle background so silent regions don't disappear into the page
        background: tokens.track,
        borderRadius: '6px',
        overflow: 'hidden',
      }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${viewWidth} ${height}`}
        preserveAspectRatio="none"
        style={{ display: 'block', pointerEvents: 'none' }}
      >
        <defs>
          {/* Played-portion gradient (bright) — matches the existing trackFill */}
          <linearGradient id="dq-wave-played" x1="0" x2="1" y1="0" y2="0">
            {tokens.text === '#E7EAF0' ? (
              <>
                <stop offset="0%"   stopColor="#1B6BE0" />
                <stop offset="50%"  stopColor="#38B6FF" />
                <stop offset="100%" stopColor="#F5B547" />
              </>
            ) : (
              <>
                <stop offset="0%"   stopColor="#1453B0" />
                <stop offset="50%"  stopColor="#1B6BE0" />
                <stop offset="100%" stopColor="#D9892C" />
              </>
            )}
          </linearGradient>
          {/* Unplayed gradient (muted) */}
          <linearGradient id="dq-wave-unplayed" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%"   stopColor={tokens.thumb} stopOpacity="0.28" />
            <stop offset="100%" stopColor={tokens.thumb} stopOpacity="0.16" />
          </linearGradient>
          {/* Clip mask: only show "played" gradient up to current progress */}
          <clipPath id="dq-wave-played-clip">
            <rect x="0" y="0" width={(progressPct / 100) * viewWidth} height={height} />
          </clipPath>
          <clipPath id="dq-wave-unplayed-clip">
            <rect
              x={(progressPct / 100) * viewWidth}
              y="0"
              width={viewWidth - (progressPct / 100) * viewWidth}
              height={height}
            />
          </clipPath>
        </defs>

        {/* Render bars twice — once for unplayed, once for played, each clipped */}
        <g clipPath="url(#dq-wave-unplayed-clip)" fill="url(#dq-wave-unplayed)">
          {Array.from(peaks).map((p, i) => {
            // Minimum bar height so silence is still visible as a thin line
            const h = Math.max(2, p * (height - 4));
            const x = i * (barWidth + barGap);
            const y = (height - h) / 2;
            return <rect key={i} x={x} y={y} width={barWidth} height={h} rx="1" />;
          })}
        </g>
        <g clipPath="url(#dq-wave-played-clip)" fill="url(#dq-wave-played)">
          {Array.from(peaks).map((p, i) => {
            const h = Math.max(2, p * (height - 4));
            const x = i * (barWidth + barGap);
            const y = (height - h) / 2;
            return <rect key={i} x={x} y={y} width={barWidth} height={h} rx="1" />;
          })}
        </g>
      </svg>

      {/* Playhead line — sharp, full-height vertical indicator */}
      {duration > 0 && (
        <div style={{
          position: 'absolute',
          top: 0, bottom: 0,
          left: `${progressPct}%`,
          width: '2px',
          marginLeft: '-1px',
          background: tokens.thumb,
          boxShadow: tokens.thumbGlow,
          pointerEvents: 'none',
        }} />
      )}

      {/* Hover tooltip — same as ScrubBar */}
      {hoverX != null && duration > 0 && (
        <div style={{
          position: 'absolute', bottom: `${height + 4}px`,
          left: `${hoverX}px`, transform: 'translateX(-50%)',
          padding: '3px 7px',
          background: tokens.text, color: tokens.shellBg,
          fontSize: '10px', fontWeight: 600,
          borderRadius: '4px',
          fontVariantNumeric: 'tabular-nums',
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
        }}>
          {formatTime(hoverTime)}
        </div>
      )}
    </div>
  );
};


const ScrubBar = ({ progressPct, currentTime, duration, tokens, disabled, onScrubStart, onScrub, onScrubEnd }) => {
  const barRef = useRef(null);
  const [hoverX, setHoverX] = useState(null);
  const [hoverTime, setHoverTime] = useState(0);

  const onPointerDown = (e) => {
    if (disabled || !barRef.current) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    onScrubStart?.();
    onScrub(e.clientX, barRef.current.getBoundingClientRect());
    const onMove = (ev) => {
      if (!barRef.current) return;
      onScrub(ev.clientX, barRef.current.getBoundingClientRect());
    };
    const onUp = () => {
      onScrubEnd?.();
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const onMove = (e) => {
    if (disabled || !barRef.current) return;
    const rect = barRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, x / rect.width));
    setHoverX(x);
    setHoverTime(ratio * (duration || 0));
  };

  return (
    <div
      ref={barRef}
      onPointerDown={onPointerDown}
      onPointerMove={onMove}
      onPointerLeave={() => setHoverX(null)}
      style={{
        position: 'relative',
        height: '20px',
        cursor: disabled ? 'default' : 'pointer',
        display: 'flex', alignItems: 'center',
        userSelect: 'none',
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {/* Track */}
      <div style={{
        position: 'absolute', inset: '8px 0',
        borderRadius: '999px',
        background: tokens.track,
      }} />
      {/* Fill */}
      <div style={{
        position: 'absolute', left: 0, top: '8px', bottom: '8px',
        width: `${progressPct}%`,
        borderRadius: '999px',
        background: tokens.trackFill,
        transition: 'width 100ms linear',
      }} />
      {/* Thumb */}
      {duration > 0 && (
        <div style={{
          position: 'absolute', top: '50%',
          left: `${progressPct}%`,
          width: '12px', height: '12px',
          marginLeft: '-6px', marginTop: '-6px',
          borderRadius: '999px',
          background: tokens.thumb,
          boxShadow: tokens.thumbGlow,
          pointerEvents: 'none',
        }} />
      )}
      {/* Hover tooltip */}
      {hoverX != null && duration > 0 && (
        <div style={{
          position: 'absolute', bottom: '24px',
          left: `${hoverX}px`, transform: 'translateX(-50%)',
          padding: '3px 7px',
          background: tokens.text, color: tokens.shellBg,
          fontSize: '10px', fontWeight: 600,
          borderRadius: '4px',
          fontVariantNumeric: 'tabular-nums',
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
        }}>
          {formatTime(hoverTime)}
        </div>
      )}
    </div>
  );
};

const PlayButton = ({ tokens, isPlaying, loading, onClick }) => (
  <button
    onClick={onClick}
    disabled={loading}
    title={isPlaying ? 'Pause (Space)' : 'Play (Space)'}
    style={{
      width: '38px', height: '38px',
      borderRadius: '50%',
      background: loading ? tokens.btnBg : `linear-gradient(135deg, ${tokens.accent}, ${tokens.thumb})`,
      border: 'none',
      color: '#FFFFFF',
      cursor: loading ? 'not-allowed' : 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: loading ? 'none' : `0 4px 14px ${tokens.accent}55`,
      flexShrink: 0,
      transition: 'transform 0.1s ease',
    }}
  >
    {loading
      ? <Loader2 size={16} style={{ animation: 'aud-spin 0.9s linear infinite' }} />
      : (isPlaying ? <Pause size={16} fill="white" /> : <Play size={16} fill="white" style={{ marginLeft: '2px' }} />)
    }
    <style>{`@keyframes aud-spin { to { transform: rotate(360deg); } }`}</style>
  </button>
);

const IconButton = ({ tokens, onClick, title, disabled, children }) => (
  <button
    onClick={onClick}
    title={title}
    disabled={disabled}
    style={{
      width: '32px', height: '32px',
      borderRadius: '8px',
      background: tokens.btnBg,
      border: `1px solid ${tokens.shellBdr}`,
      color: tokens.textSec,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.4 : 1,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      transition: 'all 0.12s ease',
      flexShrink: 0,
    }}
    onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = tokens.btnBgHover; }}
    onMouseLeave={(e) => { if (!disabled) e.currentTarget.style.background = tokens.btnBg; }}
  >
    {children}
  </button>
);

const SpeedSelector = ({ tokens, value, onChange, compact }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);
  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        onClick={() => setOpen((o) => !o)}
        title="Playback speed (, .)"
        style={{
          height: '32px',
          padding: '0 12px',
          borderRadius: '8px',
          background: open ? tokens.btnActive : tokens.btnBg,
          border: `1px solid ${tokens.shellBdr}`,
          color: open ? tokens.accent : tokens.textSec,
          cursor: 'pointer',
          fontSize: '12.5px',
          fontWeight: 600,
          fontFamily: 'inherit',
          fontVariantNumeric: 'tabular-nums',
          transition: 'all 0.12s ease',
        }}
      >
        {value === 1 ? '1.0×' : `${value}×`}
      </button>
      {open && (
        <div style={{
          position: 'absolute', bottom: 'calc(100% + 4px)',
          left: 0,
          background: tokens.text === '#E7EAF0' ? '#15181D' : '#FFFFFF',
          border: `1px solid ${tokens.shellBdr}`,
          borderRadius: '8px',
          padding: '4px',
          boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
          zIndex: 100,
          minWidth: '64px',
        }}>
          {SPEED_OPTIONS.map((s) => (
            <button
              key={s}
              onClick={() => { onChange(s); setOpen(false); }}
              style={{
                display: 'block', width: '100%',
                padding: '6px 10px',
                background: s === value ? tokens.btnActive : 'transparent',
                border: 'none',
                borderRadius: '5px',
                color: s === value ? tokens.accent : tokens.textSec,
                fontSize: '12.5px',
                fontWeight: s === value ? 600 : 400,
                fontFamily: 'inherit',
                fontVariantNumeric: 'tabular-nums',
                textAlign: 'left',
                cursor: 'pointer',
              }}
            >
              {s === 1 ? '1.0×' : `${s}×`}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Hint = ({ tokens, keys, label }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
    <kbd style={{
      padding: '1px 5px',
      background: tokens.btnBg,
      border: `1px solid ${tokens.shellBdr}`,
      borderRadius: '3px',
      fontSize: '10px',
      fontFamily: "'JetBrains Mono', ui-monospace, monospace",
      color: tokens.textSec,
    }}>
      {keys}
    </kbd>
    <span>{label}</span>
  </span>
);

export default AudioPlayer;